"use client";

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
    CheckCircle2,
    XCircle,
    Shield,
    Activity,
    Users,
    FileText,
    Building2,
    ArrowRight,
    Sparkles,
    Zap,
    TrendingDown,
    MonitorSmartphone,
    Check
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { plansRepository } from '@/lib/repositories/plans.repository';
import type { Plan } from '@/lib/types/plan';
import './marketup.css';

const MarketUpLanding: React.FC = () => {
    const router = useRouter();

    const [activeCol, setActiveCol] = useState<'mkt' | 'dmais'>('dmais');

    const { data: plan, isLoading } = useQuery({
        queryKey: ['plan', 'marketup-sp'],
        queryFn: () => plansRepository.findBySlug('marketup-sp', true), // allowHidden = true
        retry: 2,
    });

    useEffect(() => {
        if (!isLoading) {
            // Se o plano não existir ou estiver inativo (active=false), redireciona.
            // A landing exclusiva deve funcionar mesmo se visible_in_site = false
            if (!plan || !plan.active) {
                router.replace('/planos');
            }
        }
    }, [plan, isLoading, router]);

    useEffect(() => {
        // Scroll to top on load
        window.scrollTo(0, 0);

        // Mobile column toggle logic
        const interval = setInterval(() => {
            if (window.innerWidth < 768) {
                setActiveCol(prev => prev === 'dmais' ? 'mkt' : 'dmais');
            }
        }, 3000);

        return () => clearInterval(interval);
    }, []);

    const handleSubscribe = () => {
        // Redireciona para o checkout exclusivo com o slug do plano na URL
        // (O plano slug pode ser ajustado conforme necessário)
        router.push('/marketup/checkout?plan=marketup-sp');
    };

    if (isLoading || !plan || !plan.active) {
        return null;
    }

    return (
        <div className="marketup-page relative w-full font-sans">
            {/* Background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
                <div className="bg-grid-pattern opacity-20" />
                <div className="glow-orb glow-orb-1" />
                <div className="glow-orb glow-orb-2" />
            </div>

            {/* Hero Section */}
            <main className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-8 pb-16 md:pt-12 md:pb-24 flex flex-col items-center text-center">

                <div className="inline-flex items-center space-x-2 bg-[#0F172A] border border-[#32D74B]/30 px-3 py-1.5 md:px-4 md:py-2 rounded-full mb-6 fade-in-up delay-100">
                    <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-[#32D74B]" />
                    <span className="text-xs md:text-sm font-medium text-[#32D74B]">Condição Exclusiva MarketUP</span>
                </div>

                <h1 className="text-4xl md:text-6xl font-light tracking-tight mb-4 leading-tight max-w-4xl fade-in-up delay-200">
                    Saúde e Segurança do Trabalho <br className="hidden md:block" />
                    <span className="text-gradient font-normal">Simples e Digital.</span>
                </h1>

                <p className="text-lg md:text-xl font-light text-gray-400 mb-6 max-w-2xl fade-in-up delay-300">
                    O plano ideal para pequenas empresas. <br className="hidden md:block" />
                    Regularize seu negócio e envie os eventos do e-Social sem dor de cabeça.
                </p>

                {/* Hero Components Wrapper (Tags + Card) */}
                <div className="relative flex flex-col items-center justify-center w-full fade-in-up delay-400 mb-8 px-4">

                    {/* Background Watermark Tags (Left and Right) */}
                    <div className="absolute inset-0 w-full max-w-5xl mx-auto flex justify-between items-center pointer-events-none hidden lg:flex opacity-60">
                        {/* Left Side Tags */}
                        <div className="flex flex-col gap-16 -ml-16">
                            <div className="text-[#00FF87]/10 font-bold text-3xl tracking-widest uppercase transform -rotate-6 animate-pulse" style={{ animationDuration: '4s' }}>
                                PGR Integrado
                            </div>
                            <div className="text-[#00FF87]/5 font-bold text-2xl tracking-widest uppercase transform -rotate-3 ml-8 animate-pulse" style={{ animationDuration: '6s', animationDelay: '1s' }}>
                                PCMSO Completo
                            </div>
                        </div>

                        {/* Right Side Tags */}
                        <div className="flex flex-col gap-20 -mr-16 items-end">
                            <div className="text-[#00FF87]/10 font-bold text-3xl tracking-widest uppercase transform rotate-3 animate-pulse" style={{ animationDuration: '5s', animationDelay: '2s' }}>
                                Mensageria e-Social
                            </div>
                            <div className="text-[#00FF87]/5 font-bold text-2xl tracking-widest uppercase transform rotate-6 mr-4 animate-pulse" style={{ animationDuration: '7s', animationDelay: '3s' }}>
                                Treinamento NR1
                            </div>
                        </div>
                    </div>

                    {/* Pricing Card (Center) */}
                    <div className="glass-card rounded-3xl p-8 pt-14 md:p-10 max-w-sm lg:max-w-md w-full relative overflow-hidden animate-pulse-glow flex-shrink-0 z-10">
                        <div className="absolute top-0 right-0 left-0 text-center bg-[#00FF87] text-[#0B0E14] text-xs font-bold py-2 uppercase tracking-wider shadow-lg">
                            Condição especial para até 5 colaboradores
                        </div>

                        <div className="flex flex-col items-center pt-2 md:pt-0">
                            <h3 className="text-2xl font-semibold mb-2">Plano Essencial MarketUP</h3>
                            <p className="text-gray-400 mb-6 text-sm text-center">Cobertura completa para até 5 colaboradores</p>

                            <div className="flex flex-col items-center mb-6">
                                <div className="space-y-1 text-center">
                                    <span className="text-gray-500 font-medium price-strike text-lg block mb-1">De R$ 189,00</span>
                                    <div className="flex items-center justify-center space-x-2 mb-2">
                                        <Activity className="w-6 h-6 text-[#00FF87]" />
                                        <span className="text-xl font-bold tracking-tight text-white">D+ Saúde</span>
                                    </div>
                                    <span className="text-5xl font-extrabold text-white tracking-tight">R$ 129<span className="text-3xl">,90</span></span>
                                    <span className="text-gray-400 font-medium block mt-1">/mês</span>
                                </div>
                            </div>

                            <button onClick={handleSubscribe} className="btn-primary-glow w-full flex items-center justify-center space-x-2 py-3 px-6">
                                <span>Assinar Agora</span>
                                <ArrowRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </main>

            {/* Comparison Section */}
            <section className="relative z-10 w-full border-t border-white/5 bg-[#0B0E14]/80 py-24">
                <div className="max-w-5xl mx-auto px-6">
                    <div className="flex flex-col items-center justify-center mb-16 w-full">
                        {/* Red block: The problem */}
                        <div className="bg-[#EF4444]/5 border border-[#EF4444]/20 rounded-3xl p-8 md:p-10 mb-8 max-w-4xl w-full text-center relative overflow-hidden fade-in-up">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#EF4444]/50 to-transparent"></div>
                            <h2 className="text-3xl md:text-5xl font-light text-white leading-tight md:leading-snug">
                                Você prefere pagar <br className="hidden md:block" />
                                <span className="font-bold text-[#EF4444] whitespace-nowrap">R$ 40 a R$ 70</span> toda vez que <br className="hidden md:block" />
                                precisar contratar ou demitir...
                            </h2>
                        </div>

                        {/* Connector Arrow */}
                        <div className="flex justify-center mb-8 fade-in-up delay-100">
                            <div className="bg-[#0B0E14] border border-white/10 rounded-full p-4 shadow-[0_0_30px_rgba(0,255,135,0.15)]">
                                <ArrowRight className="w-8 h-8 text-[#00FF87] transform rotate-90 animate-bounce" />
                            </div>
                        </div>

                        {/* Green block: The solution */}
                        <div className="text-center fade-in-up delay-200">
                            <h2 className="text-4xl md:text-6xl font-extrabold mb-4">... ou ter <span className="text-gradient">tudo incluso?</span></h2>
                            <p className="text-gray-400 max-w-2xl mx-auto text-lg md:text-xl pt-2 font-light">Compare os custos do mercado tradicional com a cobertura completa da nossa oferta.</p>
                        </div>
                    </div>

                    <div className="glass-card rounded-2xl overflow-hidden fade-in-up">
                        <div className="overflow-x-auto">
                            <table className="compare-table w-full md:min-w-[600px] table-fixed md:table-auto">
                                <thead>
                                    <tr className="bg-white/[0.02]">
                                        <th className="w-1/2 md:w-1/3 p-4">Serviço / Documento</th>
                                        <th className={`w-1/2 md:w-1/3 !text-center border-l border-white/5 p-4 transition-all duration-500 ${activeCol === 'mkt' ? 'table-cell' : 'hidden md:table-cell'}`}>
                                            Custo Médio no Mercado
                                        </th>
                                        <th className={`w-1/2 md:w-1/3 !text-center border-l bg-[#00FF87]/10 border-[#00FF87]/20 text-[#00FF87] p-4 transition-all duration-500 ${activeCol === 'dmais' ? 'table-cell' : 'hidden md:table-cell'}`}>
                                            Com Plano D+ Saúde + MarketUP
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[
                                        { name: 'PGR (Programa de Gerenciamento de Riscos)', mkt: 'R$ 500,00', dmais: 'Isento', highlight: true },
                                        { name: 'PCMSO', mkt: 'R$ 750,00', dmais: 'Isento', highlight: true },
                                        { name: 'Exame Admissional', mkt: 'R$ 30 - 70 /vida', dmais: 'Isento' },
                                        { name: 'Exame Demissional', mkt: 'R$ 30 - 70 /vida', dmais: 'Isento' },
                                        { name: 'Exame Periódico', mkt: 'R$ 30 - 70 /vida', dmais: 'Isento' },
                                        { name: 'Exame de Retorno ao Trabalho', mkt: 'R$ 30 - 70 /vida', dmais: 'Isento' },
                                        { name: 'Portal Exclusivo p/ Agendamentos', mkt: 'R$ 2 - 7 /vida', dmais: 'Incluso no Plano' },
                                        { name: 'e-Social (Envio eventos 2210, 2220, 2240)', mkt: 'R$ 350,00 /mês', dmais: 'Incluso no Plano', highlight: true },
                                        { name: 'Treinamento NR1', mkt: 'R$ 3 - 10 /vida', dmais: 'Incluso no Plano' },
                                    ].map((item, idx) => (
                                        <tr key={idx} className="group">
                                            <td className="font-medium text-gray-200 text-sm md:text-base p-4">{item.name}</td>
                                            <td className={`text-center text-gray-500 border-l border-white/5 p-4 ${activeCol === 'mkt' ? 'table-cell' : 'hidden md:table-cell'}`}>
                                                {item.mkt}
                                            </td>
                                            <td className={`text-center font-bold border-l border-white/5 bg-[#00FF87]/[0.02] p-4 ${item.highlight ? 'text-[#00FF87]' : 'text-white'} ${activeCol === 'dmais' ? 'table-cell' : 'hidden md:table-cell'}`}>
                                                <div className="flex items-center justify-center space-x-1">
                                                    <Check className={`w-4 h-4 ${item.highlight ? 'text-[#00FF87]' : 'text-[#32D74B]'}`} />
                                                    <span className="text-sm md:text-base">{item.dmais}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Mobile dot indicators */}
                            <div className="flex justify-center space-x-3 mt-6 mb-6 md:hidden">
                                <div className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${activeCol === 'mkt' ? 'bg-[#00FF87]' : 'bg-gray-600'}`} />
                                <div className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${activeCol === 'dmais' ? 'bg-[#00FF87]' : 'bg-gray-600'}`} />
                            </div>
                        </div>

                        {/* Savings Callout */}
                        <div className="bg-gradient-to-r from-[#00FF87]/20 to-[#60EFFF]/20 border-t border-[#00FF87]/30 p-6 flex flex-col sm:flex-row items-center justify-between">
                            <div className="flex items-center space-x-4 mb-4 sm:mb-0">
                                <div className="bg-[#0B0E14] p-3 rounded-full">
                                    <TrendingDown className="w-6 h-6 text-[#00FF87]" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-white text-lg">Economia gigante todo mês!</h4>
                                    <p className="text-[#00FF87] text-sm">Pare de pagar taxas extras escondidas.</p>
                                </div>
                            </div>
                            <button onClick={handleSubscribe} className="bg-white text-[#0B0E14] hover:bg-gray-100 font-bold py-2 px-6 rounded-full transition-colors whitespace-nowrap">
                                Garantir Benefício
                            </button>
                        </div>
                    </div>

                </div>
            </section>

            {/* Benefits Grid */}
            <section className="relative z-10 w-full py-24 px-6 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="glass-card p-8 rounded-2xl flex flex-col items-center text-center fade-in-up">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00FF87]/20 to-[#60EFFF]/20 flex items-center justify-center mb-6">
                            <MonitorSmartphone className="w-7 h-7 text-[#00FF87]" />
                        </div>
                        <h3 className="text-xl font-bold mb-3">100% Digital</h3>
                        <p className="text-gray-400 text-sm">Gestão completa pelo seu portal exclusivo. Agende exames e acesse laudos de qualquer lugar.</p>
                    </div>

                    <div className="glass-card p-8 rounded-2xl flex flex-col items-center text-center fade-in-up delay-100">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#32D74B]/20 to-[#00C6FF]/20 flex items-center justify-center mb-6">
                            <Zap className="w-7 h-7 text-[#32D74B]" />
                        </div>
                        <h3 className="text-xl font-bold mb-3">Conformidade Imediata</h3>
                        <p className="text-gray-400 text-sm">Sua empresa regularizada com as NRs e livre de multas trabalhistas em poucos cliques.</p>
                    </div>

                    <div className="glass-card p-8 rounded-2xl flex flex-col items-center text-center fade-in-up delay-200">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-6">
                            <Users className="w-7 h-7 text-purple-400" />
                        </div>
                        <h3 className="text-xl font-bold mb-3">Foco em Pequenos</h3>
                        <p className="text-gray-400 text-sm">Feito sob medida para negócios com até 5 vidas, garantindo o melhor custo-benefício do mercado.</p>
                    </div>
                </div>
            </section>

            {/* Footer CTA */}
            <footer className="relative z-10 w-full py-16 text-center px-6">
                <h2 className="text-2xl md:text-3xl font-bold mb-6">Pronto para transformar a gestão de SST da sua empresa?</h2>
                <button onClick={handleSubscribe} className="btn-primary-glow inline-flex items-center space-x-2">
                    <span>Assinar o Plano Agora</span>
                    <ArrowRight className="w-5 h-5" />
                </button>
                <p className="mt-8 text-sm text-gray-500">
                    © {new Date().getFullYear()} D+ Saúde. Exclusivo para clientes MarketUP.
                </p>
            </footer>
        </div>
    );
};

export default MarketUpLanding;
